import { Fragment } from 'react';
import { useLocation, useMatches, Link } from 'react-router-dom';
import { usePageSettingsStore } from '../../store/usePageSettingsStore';
import { resolvePageMeta } from './pageMeta';

interface RouteHandle {
  breadcrumb?: string;
}

interface Crumb {
  label: string;
  pathname: string;
}

/** Liste parente des fiches de détail dont la route n'est pas imbriquée. */
const PARENTS: Record<string, Crumb> = {
  dossiers: { label: 'Dossiers', pathname: '/dossiers' },
  tickets: { label: 'Demandes', pathname: '/tickets' },
  contacts: { label: 'Répertoire', pathname: '/contacts' },
  facturation: { label: 'Facturation', pathname: '/facturation' },
  parapheur: { label: 'Parapheur', pathname: '/parapheur' },
};

export function BreadcrumbNav() {
  const matches = useMatches();
  const { pathname } = useLocation();
  const pageCrumb = usePageSettingsStore((s) => s.pageCrumb);

  let crumbs: Crumb[] = matches
    .filter((match) => (match.handle as RouteHandle | undefined)?.breadcrumb !== undefined)
    .map((match) => ({
      label: (match.handle as RouteHandle).breadcrumb ?? '',
      pathname: match.pathname,
    }));

  const only = crumbs.length === 1 ? crumbs[0] : undefined;
  if (only) {
    const segments = pathname.split('/').filter(Boolean);
    const parent = PARENTS[segments[0] ?? ''];
    if (parent && segments.length >= 2) {
      crumbs = [parent, { label: pageCrumb ?? only.label, pathname }];
    } else {
      // À la racine, le fil reprend le nom de la page : il reste présent pour
      // que le titre ne change pas de hauteur d'une page à l'autre.
      crumbs = [{ label: resolvePageMeta(pathname)?.title ?? only.label, pathname }];
    }
  }

  const alone = crumbs.length === 1;

  return (
    <nav
      aria-label="Fil d'Ariane"
      className="flex items-center gap-1 min-h-[18px] text-[13px] leading-[18px] whitespace-nowrap overflow-hidden"
    >
      {crumbs.map((crumb, index) => {
        const isLast = index === crumbs.length - 1;
        return (
          <Fragment key={crumb.pathname}>
            {index > 0 ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 320 512"
                className="w-2.5 h-2.5 text-neutral-400 flex-shrink-0"
                fill="currentColor"
                aria-hidden
              >
                {/* Font Awesome Pro 6.5.1 — chevron-right regular */}
                <path d="M305 239c9.4 9.4 9.4 24.6 0 33.9L113 465c-9.4 9.4-24.6 9.4-33.9 0s-9.4-24.6 0-33.9l175-175L79 81c-9.4-9.4-9.4-24.6 0-33.9s24.6-9.4 33.9 0L305 239z" />
              </svg>
            ) : null}
            {isLast ? (
              <span
                className={
                  alone
                    ? 'font-medium text-neutral-400 truncate'
                    : 'font-medium text-cime-text-primary truncate'
                }
                aria-current="page"
              >
                {crumb.label}
              </span>
            ) : (
              <Link
                to={crumb.pathname}
                className="text-neutral-500 hover:text-cime-text-primary transition-colors flex-shrink-0"
              >
                {crumb.label}
              </Link>
            )}
          </Fragment>
        );
      })}
    </nav>
  );
}
